// hooks.ts
import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import {
    reportApi,
    GetAdvertiserReportGraphResponse,
    GetAffiliateReportGraphResponse,
    GetCascadeReportGraphResponse,
    GetOfferReportGraphResponse,
    GetPerformanceGraphResponse,
} from "api/TenantSlug/Report/api";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import GetPerformanceReportRequest = App.Http.Requests.Report.GetPerformanceReportRequest;
import GetPerformanceReportResponse = App.Http.Responses.Report.GetPerformanceReportResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetCascadeReportRequest = App.Http.Requests.Report.GetCascadeReportRequest;
import GetCascadeReportResponse = App.Http.Responses.Report.GetCascadeReportResponse;
import GetCascadeReportExportRequest = App.Http.Requests.Report.GetCascadeReportExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

/**
 * Hook to get the performance report
 */
export const useGetPerformanceReportQuery = (request: GetPerformanceReportRequest, options = {}) => {
    return useQueryApi<GetPerformanceReportResponse>({
        queryKey: ["getPerformanceReport", request],
        queryFn: () => reportApi.getPerformance(request),
        ...options,
    });
};

export const useGetPerformanceGraphQuery = (
    request: GetPerformanceReportRequest,
    options?: Omit<
        UseQueryOptions<GetPerformanceGraphResponse, AxiosError, GetPerformanceGraphResponse>,
        "queryKey" | "queryFn"
    >
) => {
    return useQueryApi<GetPerformanceGraphResponse, AxiosError>({
        queryKey: ["getPerformanceGraph", request],
        queryFn: () => reportApi.getPerformanceGraph(request),
        ...options,
    });
};

/**
 * Hook to export the performance report
 */
export const useGetPerformanceReportExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["PerformanceReportExport"],
        mutationFn: (request) => reportApi.exportPerformance(request),
        ...options,
    });
};

/**
 * Hook to get the performance offers report
 */
export const useGetPerformanceOffersReportQuery = (request: GetPerformanceReportRequest, options = {}) => {
    return useQueryApi<GetPerformanceReportResponse>({
        queryKey: ["getPerformanceOffersReport", request],
        queryFn: () => reportApi.getPerformanceOffers(request),
        ...options,
    });
};

export const useGetPerformanceOffersGraphQuery = (
    request: GetPerformanceReportRequest,
    options?: Omit<
        UseQueryOptions<GetOfferReportGraphResponse, AxiosError, GetOfferReportGraphResponse>,
        "queryKey" | "queryFn"
    >
) => {
    return useQueryApi<GetOfferReportGraphResponse, AxiosError>({
        queryKey: ["getPerformanceOffersGraph", request],
        queryFn: () => reportApi.getPerformanceOffersGraph(request),
        ...options,
    });
};

/**
 * Hook to export the performance offers report
 */
export const useGetPerformanceOffersReportExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["PerformanceOffersReportExport"],
        mutationFn: (request) => reportApi.exportPerformanceOffers(request),
        ...options,
    });
};

/**
 * Hook to get the performance affiliates report
 */
export const useGetPerformanceAffiliatesReportQuery = (request: GetPerformanceReportRequest, options = {}) => {
    return useQueryApi<GetPerformanceReportResponse>({
        queryKey: ["getPerformanceAffiliatesReport", request],
        queryFn: () => reportApi.getPerformanceAffiliates(request),
        ...options,
    });
};

export const useGetPerformanceAffiliatesGraphQuery = (
    request: GetPerformanceReportRequest,
    options?: Omit<
        UseQueryOptions<GetAffiliateReportGraphResponse, AxiosError, GetAffiliateReportGraphResponse>,
        "queryKey" | "queryFn"
    >
) => {
    return useQueryApi<GetAffiliateReportGraphResponse, AxiosError>({
        queryKey: ["getPerformanceAffiliatesGraph", request],
        queryFn: () => reportApi.getPerformanceAffiliatesGraph(request),
        ...options,
    });
};

/**
 * Hook to export the performance affiliates report
 */
export const useGetPerformanceAffiliatesReportExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["PerformanceAffiliatesReportExport"],
        mutationFn: (request) => reportApi.exportPerformanceAffiliates(request),
        ...options,
    });
};

/**
 * Hook to get the performance advertisers report
 */
export const useGetPerformanceAdvertisersReportQuery = (request: GetPerformanceReportRequest, options = {}) => {
    return useQueryApi<GetPerformanceReportResponse>({
        queryKey: ["getPerformanceAdvertisersReport", request],
        queryFn: () => reportApi.getPerformanceAdvertisers(request),
        ...options,
    });
};

export const useGetPerformanceAdvertisersGraphQuery = (
    request: GetPerformanceReportRequest,
    options?: Omit<
        UseQueryOptions<GetAdvertiserReportGraphResponse, AxiosError, GetAdvertiserReportGraphResponse>,
        "queryKey" | "queryFn"
    >
) => {
    return useQueryApi<GetAdvertiserReportGraphResponse, AxiosError>({
        queryKey: ["getPerformanceAdvertisersGraph", request],
        queryFn: () => reportApi.getPerformanceAdvertisersGraph(request),
        ...options,
    });
};

/**
 * Hook to export the performance advertisers report
 */
export const useGetPerformanceAdvertisersReportExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["PerformanceAdvertisersReportExport"],
        mutationFn: (request) => reportApi.exportPerformanceAdvertisers(request),
        ...options,
    });
};

/**
 * Hook to get the cascades report
 */
export const useGetCascadesReportQuery = (request: GetCascadeReportRequest, options = {}) => {
    return useQueryApi<GetCascadeReportResponse>({
        queryKey: ["getCascadesReport", request],
        queryFn: () => reportApi.getCascades(request),
        ...options,
    });
};

export const useGetCascadesGraphQuery = (
    request: GetCascadeReportRequest,
    options?: Omit<
        UseQueryOptions<GetCascadeReportGraphResponse, AxiosError, GetCascadeReportGraphResponse>,
        "queryKey" | "queryFn"
    >
) => {
    return useQueryApi<GetCascadeReportGraphResponse, AxiosError>({
        queryKey: ["getCascadesGraph", request],
        queryFn: (): Promise<GetCascadeReportGraphResponse> => reportApi.getCascadesGraph(request),
        ...options,
    });
};

export const useGetCascadeGraphQuery = (
    id: string,
    request: GetCascadeReportRequest,
    options?: Omit<
        UseQueryOptions<GetCascadeReportGraphResponse, AxiosError, GetCascadeReportGraphResponse>,
        "queryKey" | "queryFn"
    >
) => {
    return useQueryApi<GetCascadeReportGraphResponse, AxiosError>({
        queryKey: ["getCascadeGraph", request, id],
        queryFn: (): Promise<GetCascadeReportGraphResponse> => reportApi.getCascadeGraph(id, request),
        ...options,
    });
};

/**
 * Hook to export the cascades report
 */
export const useGetCascadesExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, GetCascadeReportExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, GetCascadeReportExportRequest>({
        mutationKey: ["getCascadesExport"],
        mutationFn: (request) => reportApi.exportCascades(request),
        ...options,
    });
};

/**
 * Hook to get the cascade channels report
 */
export const useGetCascadeChannelsReportQuery = (cascadeId: string, request: GetCascadeReportRequest, options = {}) => {
    return useQueryApi<GetCascadeReportResponse>({
        queryKey: ["getCascadeChannelsReport", request],
        queryFn: () => reportApi.getCascadeChannels(cascadeId, request),
        ...options,
    });
};

/**
 * Hook to export the cascade channels report
 */
export const useGetCascadeChannelsReportExportMutation = (
    cascadeId: string,
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, GetCascadeReportExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, GetCascadeReportExportRequest>({
        mutationKey: ["getCascadeChannelsReportExport", cascadeId],
        mutationFn: (request) => reportApi.exportCascadeChannels(cascadeId, request),
        ...options,
    });
};
